/**
 * loadDefinitions.js — EnergyPlus-aligned internal/external load model.
 *
 * A load definition is an explicit object (like a real EnergyPlus load object)
 * that targets a Zone or a ZoneList (a Group). One registry drives three things:
 *   1. the Add/Edit forms on the Loads screen,
 *   2. validation,
 *   3. the IDF emission (idfGenerator → emitLoadDefinitions).
 *
 * Shape of a definition:
 *   { id, type, name, target:{kind,id}, method, value, schedule, params:{…} }
 *   - zoned types (occupancy/lighting/equipment/infiltration) carry a target;
 *     exterior_lighting / exterior_equipment are facility-level (no target).
 *   - method ∈ the type's METHODS; value is the number for that method.
 *   - params holds the advanced EnergyPlus fractions (sensible defaults below).
 */

let _seq = 0;
const uid = () => `load_${Date.now().toString(36)}_${(_seq++).toString(36)}`;

// Schedules come from the Schedules screen (see loadScheduleOptions.js). A load's
// `schedule` field is 'auto' (the schedule the Schedules cascade resolves for the
// target), 'always_on' / 'always_off', or a scope token ('building' | 'floor:<id>'
// | 'zone:<id>'). The dropdown options and IDF profiles are derived from there.

// ── Type registry ─────────────────────────────────────────────────────────────
export const LOAD_TYPES = {
  occupancy: {
    id: 'occupancy', label: 'Occupancy', eplus: 'People', zoned: true,
    blurb: 'People — heat gain and outdoor-air driver',
    methods: [
      { value: 'area_person',  label: 'Area per Person',   unit: 'm²/person',  eplus: 'Area/Person' },
      { value: 'people_area',  label: 'People per Area',    unit: 'people/m²',  eplus: 'People/Area' },
      { value: 'people',       label: 'Number of People',   unit: 'people',     eplus: 'People' },
    ],
    defaultMethod: 'area_person',
    defaultValue: 10,
    defaultSchedule: 'auto',
    params: [
      { key: 'activity_level',   label: 'Activity Level',   unit: 'W/person', default: 120, min: 60, max: 500, step: 5 },
      { key: 'fraction_radiant', label: 'Fraction Radiant', unit: '',         default: 0.3, min: 0,  max: 1,   step: 0.05 },
    ],
  },
  interior_lighting: {
    id: 'interior_lighting', label: 'Interior Lighting', eplus: 'Lights', zoned: true,
    blurb: 'Interior lighting power and schedule',
    methods: [
      { value: 'watts_area',   label: 'Power Density (LPD)', unit: 'W/m²',     eplus: 'Watts/Area' },
      { value: 'level',        label: 'Total Lighting Power', unit: 'W',        eplus: 'LightingLevel' },
      { value: 'watts_person', label: 'Power per Person',     unit: 'W/person', eplus: 'Watts/Person' },
    ],
    defaultMethod: 'watts_area',
    defaultValue: 8,
    defaultSchedule: 'auto',
    params: [
      { key: 'fraction_radiant',    label: 'Fraction Radiant',    unit: '', default: 0.32, min: 0, max: 1, step: 0.01 },
      { key: 'fraction_visible',    label: 'Fraction Visible',    unit: '', default: 0.25, min: 0, max: 1, step: 0.01 },
      { key: 'return_air_fraction', label: 'Return Air Fraction', unit: '', default: 0.0,  min: 0, max: 1, step: 0.01 },
    ],
  },
  exterior_lighting: {
    id: 'exterior_lighting', label: 'Exterior Lighting', eplus: 'Exterior:Lights', zoned: false,
    blurb: 'Facade / site lighting (facility-level)',
    methods: [{ value: 'level', label: 'Design Level', unit: 'W', eplus: 'LightingLevel' }],
    defaultMethod: 'level',
    defaultValue: 1000,
    defaultSchedule: 'auto',
    params: [
      { key: 'control_option', label: 'Control Option', type: 'select', default: 'ScheduleNameOnly',
        options: [{ value: 'ScheduleNameOnly', label: 'Schedule Only' }, { value: 'AstronomicalClock', label: 'Astronomical Clock' }] },
    ],
  },
  interior_equipment: {
    id: 'interior_equipment', label: 'Interior Equipment', eplus: 'ElectricEquipment', zoned: true,
    blurb: 'Plug / process electric equipment',
    methods: [
      { value: 'watts_area',   label: 'Power Density (EPD)',  unit: 'W/m²',     eplus: 'Watts/Area' },
      { value: 'level',        label: 'Total Equipment Power', unit: 'W',        eplus: 'EquipmentLevel' },
      { value: 'watts_person', label: 'Power per Person',      unit: 'W/person', eplus: 'Watts/Person' },
    ],
    defaultMethod: 'watts_area',
    defaultValue: 10,
    defaultSchedule: 'auto',
    params: [
      { key: 'fraction_latent',  label: 'Fraction Latent',  unit: '', default: 0.0, min: 0, max: 1, step: 0.01 },
      { key: 'fraction_radiant', label: 'Fraction Radiant', unit: '', default: 0.5, min: 0, max: 1, step: 0.01 },
      { key: 'fraction_lost',    label: 'Fraction Lost',    unit: '', default: 0.0, min: 0, max: 1, step: 0.01 },
    ],
  },
  exterior_equipment: {
    id: 'exterior_equipment', label: 'Exterior Equipment', eplus: 'Exterior:FuelEquipment', zoned: false,
    blurb: 'Site equipment (pumps, signage, …) — facility-level',
    methods: [{ value: 'level', label: 'Design Level', unit: 'W', eplus: 'EquipmentLevel' }],
    defaultMethod: 'level',
    defaultValue: 500,
    defaultSchedule: 'auto',
    params: [
      { key: 'fuel_type', label: 'Fuel Type', type: 'select', default: 'Electricity',
        options: ['Electricity', 'NaturalGas', 'Propane', 'Diesel', 'FuelOilNo1', 'FuelOilNo2'].map((v) => ({ value: v, label: v })) },
    ],
  },
  infiltration: {
    id: 'infiltration', label: 'Infiltration', eplus: 'ZoneInfiltration:DesignFlowRate', zoned: true,
    blurb: 'Uncontrolled outdoor air leakage',
    methods: [
      { value: 'ach',          label: 'Air Changes per Hour', unit: 'ACH',       eplus: 'AirChanges/Hour' },
      { value: 'flow_area',    label: 'Flow per Floor Area',  unit: 'm³/s·m²',   eplus: 'Flow/Area' },
      { value: 'flow_ext',     label: 'Flow per Exterior Area', unit: 'm³/s·m²', eplus: 'Flow/ExteriorArea' },
      { value: 'flow_zone',    label: 'Flow per Zone',        unit: 'm³/s',      eplus: 'Flow/Zone' },
    ],
    defaultMethod: 'ach',
    defaultValue: 0.5,
    defaultSchedule: 'auto',
    params: [],
  },
};

export const LOAD_TYPE_ORDER = [
  'occupancy', 'interior_lighting', 'exterior_lighting',
  'interior_equipment', 'exterior_equipment', 'infiltration',
];

export const getType = (type) => LOAD_TYPES[type] || null;
export const isZoned = (type) => !!LOAD_TYPES[type]?.zoned;
export const methodsFor = (type) => LOAD_TYPES[type]?.methods || [];
export const methodMeta = (type, method) => methodsFor(type).find((m) => m.value === method) || null;

// ── Factory ───────────────────────────────────────────────────────────────────
export function createLoad(type, overrides = {}) {
  const t = LOAD_TYPES[type];
  if (!t) throw new Error(`Unknown load type: ${type}`);
  const params = {};
  for (const p of t.params) params[p.key] = p.default;
  return {
    id: uid(),
    type,
    name: '',                                   // auto-named in the store if blank
    target: t.zoned ? { kind: 'building', id: null } : { kind: 'facility', id: null },
    method: t.defaultMethod,
    value: t.defaultValue,
    schedule: t.defaultSchedule,
    params,
    ...overrides,
  };
}

/** Ensure params match the type registry (fills missing defaults; drops stale on type/method use). */
export function withDefaults(def) {
  const t = LOAD_TYPES[def.type];
  if (!t) return def;
  const params = { ...def.params };
  for (const p of t.params) if (params[p.key] == null) params[p.key] = p.default;
  return { ...def, params };
}

// ── Validation ────────────────────────────────────────────────────────────────
/** @returns {string[]} list of human-readable problems (empty = valid). */
export function validateLoad(def, { zones = [], groups = [] } = {}) {
  const errors = [];
  const t = LOAD_TYPES[def.type];
  if (!t) return ['Unknown load type.'];
  if (!def.name || !def.name.trim()) errors.push('Name is required.');
  if (!methodMeta(def.type, def.method)) errors.push('Choose a calculation method.');
  if (def.value == null || Number.isNaN(Number(def.value)) || Number(def.value) < 0) {
    errors.push('Enter a valid (non-negative) value.');
  }
  if (!def.schedule) errors.push('Select a schedule.');
  if (t.zoned) {
    const k = def.target?.kind;
    if (k === 'zone' && !zones.some((z) => z.id === def.target.id)) errors.push('Select a valid zone.');
    else if (k === 'zonelist' && !groups.some((g) => g.id === def.target.id)) errors.push('Select a valid group.');
    else if (!['zone', 'zonelist', 'building'].includes(k)) errors.push('Select a target.');
    if (k === 'zonelist') {
      const g = groups.find((gg) => gg.id === def.target.id);
      if (g && (!g.zoneIds || g.zoneIds.length === 0)) errors.push('The selected group has no zones.');
    }
  }
  return errors;
}

// ── Duplicate-load detection ──────────────────────────────────────────────────
/** Zone IDs a zoned load applies to (building → all, zone → one, zonelist → group). */
export function loadZoneSet(load, groups = [], allZoneIds = []) {
  const k = load.target?.kind;
  if (k === 'building') return new Set(allZoneIds);
  if (k === 'zone') return new Set(load.target.id ? [load.target.id] : []);
  if (k === 'zonelist') return new Set((groups.find((g) => g.id === load.target.id)?.zoneIds) || []);
  return new Set();
}

/**
 * Find an existing same-type zoned load whose zones overlap `draft` — i.e. a zone
 * that would receive the same load type twice (EnergyPlus sums them → double-count).
 * Facility loads (exterior) may legitimately repeat, so they never conflict.
 * @returns {{ load, overlap:string[] } | null}
 */
export function findLoadConflict(loads, draft, excludeId, groups = [], allZoneIds = []) {
  if (!isZoned(draft.type)) return null;
  const ds = loadZoneSet(draft, groups, allZoneIds);
  if (ds.size === 0) return null;
  for (const l of loads) {
    if (l.id === excludeId || l.type !== draft.type) continue;
    const os = loadZoneSet(l, groups, allZoneIds);
    const overlap = [...ds].filter((id) => os.has(id));
    if (overlap.length) return { load: l, overlap };
  }
  return null;
}

// ── Display helpers (UI) ──────────────────────────────────────────────────────
/** Human label for a definition's target. */
export function targetLabel(def, { zones = [], groups = [] } = {}) {
  if (!isZoned(def.type)) return 'Facility';
  const k = def.target?.kind;
  if (k === 'building') return 'Entire Building';
  if (k === 'zone') return zones.find((z) => z.id === def.target.id)?.name || '— missing zone —';
  if (k === 'zonelist') return groups.find((g) => g.id === def.target.id)?.name || '— missing group —';
  return '—';
}

/** Short "method = value unit" summary for a row. */
export function valueSummary(def) {
  const m = methodMeta(def.type, def.method);
  if (!m) return `${def.value}`;
  return `${def.value} ${m.unit}`.trim();
}
